import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { BaseSchemaEditor } from '@client/components/dynamic-form/SchemaEditor/BaseSchemaEditor';
import { BaseSchemaPreview } from '@client/components/dynamic-form/SchemaEditor/BaseSchemaPreview';
import { FlowView } from '@client/components/dynamic-form/visual-builder/flow-view/FlowView';
import { FormHealthPopover } from '@client/components/dynamic-form/visual-builder/form-health/FormHealthPopover';
import {
    type FormHealthTarget,
    resolveFormHealth,
} from '@client/components/dynamic-form/visual-builder/form-health/resolve-form-health';
import { useBuilderValidation } from '@client/components/dynamic-form/visual-builder/hooks/use-builder-validation';
import { LogicOverview } from '@client/components/dynamic-form/visual-builder/logic-view/LogicOverview';
import { SAMPLE_SCHEMA } from '@client/components/dynamic-form/visual-builder/sample-data/insurance-claim-sample-schema';
import { type BuildInspectorTarget, VisualBuilder } from '@client/components/dynamic-form/visual-builder/VisualBuilder';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDesignerSchema } from '@client/hooks/form-designer/useFormDesignerSchema';
import { useSerialAutosave } from '@client/hooks/form-designer/useSerialAutosave';
import { useUnsavedChangesPrompt } from '@client/hooks/forms/useUnsavedChangesPrompt';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { getStatusLabel } from '@client/utils/status/status-config';
import { builderSchemaToRuntime } from '@shared/schemas/dynamic-forms/builder-schema';
import { type DehydratedFormSchema, dehydrateRuntimeSchema } from '@shared/schemas/dynamic-forms/schema-normaliser';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Drawer, Form, Input, Select } from 'antd';
import {
    ArrowLeft,
    Blocks,
    Braces,
    Eye,
    FlaskConical,
    History as HistoryIcon,
    ListChecks,
    Maximize2,
    Minimize2,
    Redo2,
    Rocket,
    Save,
    Settings,
    Undo2,
    Workflow,
} from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { refreshFormDefinitionOnAutosaveConflict } from './autosave-conflict';
import { resolveNewDefinitionIdentity, toDefinitionName, toSchemaKey } from './form-definition-identity';
import { HistoryDrawer } from './HistoryDrawer';
import { applyInlineFormRename, InlineFormNameEditor } from './InlineFormNameEditor';
import { useDeferredDefinitionNameRename } from './useDeferredDefinitionNameRename';
import {
    type DefinitionDetailsFormValues,
    normaliseTags,
    synchroniseSavedDefinitionDetails,
    useDefinitionDetailsFormSync,
} from './useDefinitionDetailsFormSync';

type RuntimeSchemaInput = RouterInputs['formDesigner']['createFormDefinition']['runtimeSchema'];

const EDITOR_VIEWS = ['build', 'flow', 'logic', 'json', 'settings'] as const;
type EditorView = (typeof EDITOR_VIEWS)[number];

type FormDesignerSearch = {
    view?: EditorView;
};

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId/')(
    {
        staticData: {
            // No `metadata.title` here on purpose: it would add a third, redundant
            // crumb ("Edit form") after the parent's, which already ends the trail
            // at the form's own name.
            ui: {
                // Full-bleed workspace (compliance-audit pattern): strip the page
                // canvas padding so the editor fills the content area edge to edge.
                className: 'p-0',
                heightVariant: 'fit',
                // The editor is a full-bleed workspace, not a page with a header. The parent
                // route's `useTitleForChildren` would otherwise stamp a "Form designer" title
                // chip above it — which the breadcrumb already says, and which the form's own
                // name repeats one row below.
                hideTitle: true,
            },
        },
        validateSearch: (search: Record<string, unknown>): FormDesignerSearch => ({
            view: EDITOR_VIEWS.includes(search.view as EditorView) ? (search.view as EditorView) : undefined,
        }),
        component: FormDefinitionEditor,
    },
);

const DEFAULT_SCHEMA: DehydratedFormSchema = {
    title: 'New form',
    description: '',
    pages: [
        {
            name: 'page-1',
            title: 'Page 1',
            elements: [
                {
                    type: 'text',
                    name: 'example-field',
                    title: 'Example field',
                },
            ],
        },
    ],
};

function FormDefinitionEditor() {
    const { orgId, formDefinitionId } = Route.useParams();
    const { view: viewParam } = Route.useSearch();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const formId = useId();
    const isNew = formDefinitionId === 'new';

    const view: EditorView = viewParam ?? 'build';
    // Ref mirror of the active view — read by the store subscription below so a
    // programmatic sync while leaving Settings never marks the schema dirty.
    const viewRef = useRef(view);
    viewRef.current = view;

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFullScreen, setPreviewFullScreen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
    const [buildInspectorTarget, setBuildInspectorTarget] = useState<BuildInspectorTarget>();
    /** Bumped when something asks the header to open its title editor. */
    const [renameRequestId, setRenameRequestId] = useState(0);

    // Fetch existing definition
    const {
        data: definition,
        isLoading,
        error: definitionError,
    } = trpc.formDesigner.getFormDefinition.useQuery(
        isNew ? skipToken : { formDefinitionId: BigInt(formDefinitionId) },
    );

    const latestVersion = definition?.Versions[0];
    const {
        data: versionHistory,
        hasNextPage: hasMoreVersions,
        fetchNextPage: fetchMoreVersions,
        isFetchingNextPage: isFetchingMoreVersions,
        isFetchNextPageError,
        isLoading: isLoadingVersionHistory,
        error: versionHistoryError,
        refetch: refetchVersionHistory,
    } = trpc.formDesigner.listVersionHistory.useInfiniteQuery(
        { formDefinitionId: isNew ? 0n : BigInt(formDefinitionId), limit: 20 },
        {
            enabled: historyOpen && !isNew,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );
    const versionHistoryRows = versionHistory?.pages.flatMap((page) => page.versions) ?? [];

    // Schema hook with dual-state model
    const {
        editorJson,
        hydrated,
        dehydrated,
        jsonSchemaValidation,
        parseError,
        parseErrorKind,
        copied,
        formStore,
        builderStore,
        authoritativeSource,
        isDirty,
        isStaleSnapshot,
        hasRemoteUpdate,
        editVersion,
        setEditorJson,
        renameFormTitle,
        formatJson,
        copySchema,
        getSchemaForSave,
        onSaveSuccess: markSchemaSaved,
        syncToStore,
        syncFromStore,
        initializeFromFetched,
        getExpectedSnapshotHash,
        markPersistedSnapshot,
        reloadRemoteSnapshot,
        keepLocalChanges,
    } = useFormDesignerSchema({
        formDefinitionId: isNew ? 'new' : formDefinitionId,
        version: latestVersion?.version ?? 1,
        initialSchema: isNew ? DEFAULT_SCHEMA : undefined,
        previewActive: previewOpen,
    });

    // Guard against losing unsaved edits on navigation / refresh / tab close (H2).
    // A ref (not state) so it can be cleared synchronously before the post-create
    // programmatic navigate below, without waiting for a re-render.
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    useUnsavedChangesPrompt(isDirtyRef);

    // Builder-level validation — computed once here and passed into the views so
    // the schema is validated a single time per change. Gates the Publish action
    // client-side; the server re-validates authoritatively on publish, so this
    // is UX, not the security gate.
    const builderValidation = useBuilderValidation(builderStore);
    const schemaHasErrors =
        authoritativeSource === 'json' ? jsonSchemaValidation?.isValid !== true : builderValidation.hasErrors;
    // The form title a respondent sees. The header shows this rather than the
    // internal definition name, so the editor and the rendered form never
    // disagree about what the form is called — which means reading it from
    // whichever representation is authoritative, not always from the builder
    // store (it only re-syncs on a view change).
    const builderSchema = useStore(builderStore, (s) => s.schema);
    const schemaTitle =
        authoritativeSource === 'json' ? (dehydrated?.title ?? builderSchema.title) : builderSchema.title;

    // Every problem, resolved to a location and a destination — the data behind
    // the header's health chip.
    const healthIssues = useMemo(() => {
        if (authoritativeSource !== 'json') return resolveFormHealth(builderSchema, builderValidation.warnings);
        // Unparseable (or P0-invalid) JSON produces no issue list at all. An empty
        // list reads as "All checks pass" while save and publish are both blocked,
        // so the failure is modelled as the error it is, pointing at the editor
        // that can fix it.
        if (!dehydrated) {
            return [
                {
                    id: 'json:invalid',
                    severity: 'error' as const,
                    message: parseError ?? 'The JSON source could not be read',
                    location: 'JSON source',
                    target: { kind: 'json' as const },
                },
            ];
        }
        // Resolved against the live parsed document, not the builder snapshot:
        // pages inserted or reordered in JSON would otherwise mislabel every
        // issue after them and jump to the wrong page.
        return resolveFormHealth(dehydrated, jsonSchemaValidation?.issues, { titleTarget: { kind: 'json' } });
    }, [builderSchema, authoritativeSource, dehydrated, parseError, jsonSchemaValidation, builderValidation.warnings]);

    // Schema history — surfaced as undo/redo in the header row.
    const canUndo = useStore(builderStore, (s) => s.canUndo);
    const canRedo = useStore(builderStore, (s) => s.canRedo);

    // Load fetched schema into both representations.
    // NOTE: must stay declared BEFORE the dirty-marking subscription effect so the
    // initial setSchema on mount runs before the subscription is registered.
    useEffect(() => {
        if (latestVersion && !isNew) {
            const schema = latestVersion.runtimeSchema as DehydratedFormSchema;
            initializeFromFetched(schema, latestVersion.version, latestVersion.snapshotHash);
        }
    }, [latestVersion, isNew, initializeFromFetched]);

    // Dirty tracking lives inside useFormDesignerSchema — its subscription
    // distinguishes user edits from the hook's own programmatic syncs.

    // View change — runs the JSON ⇄ builder sync before the URL updates.
    const handleViewChange = useCallback(
        (newView: EditorView) => {
            const prevView = viewRef.current;
            if (prevView === newView) return;

            if (prevView === 'json' && authoritativeSource === 'json') {
                syncToStore();
            } else if (newView === 'json' && authoritativeSource === 'visual-builder') {
                syncFromStore();
            }

            // Cast mirrors SegmentedNavigation — generic navigation cannot infer
            // this route's search param types.
            void (navigate as (opts: Record<string, unknown>) => void)({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    view: newView === 'build' ? undefined : newView,
                }),
                replace: true,
            });
        },
        [authoritativeSource, syncToStore, syncFromStore, navigate],
    );

    // Details form state
    const [detailsForm] = Form.useForm<DefinitionDetailsFormValues>();
    useDefinitionDetailsFormSync(detailsForm, definition);

    // Mutations
    const { mutate: createMutate, isPending: isCreating } = trpc.formDesigner.createFormDefinition.useMutation({
        onSuccess: (result) => {
            message.success('Form definition created');
            markSchemaSaved();
            // Clear the dirty ref synchronously so the unsaved-changes blocker does
            // not prompt on this programmatic post-create navigate.
            isDirtyRef.current = false;
            void trpcUtils.formDesigner.listFormDefinitions.invalidate();
            navigate({
                to: '/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId',
                params: { orgId, formDefinitionId: result.definition.id.toString() },
            });
        },
        onError: (error) => errorMessage.show(error),
    });

    const { mutateAsync: autosaveMutateAsync } = trpc.formDesigner.updateDraft.useMutation();

    const { mutate: updateDetailsMutate } = trpc.formDesigner.updateDetails.useMutation({
        onSuccess: (updated, submitted) => {
            synchroniseSavedDefinitionDetails(detailsForm, updated, submitted);
            message.success('Details updated');
            void trpcUtils.formDesigner.getFormDefinition.invalidate();
            void trpcUtils.formDesigner.listVersionHistory.invalidate();
            void trpcUtils.history.listHistoryItems.invalidate();
        },
        onError: (error) => errorMessage.show(error),
    });
    // Header renames follow the schema autosave in the background. Keep this
    // mutation silent: the autosave already refreshes the definition, and the
    // explicit Settings action owns the "Details updated" confirmation.
    const { mutateAsync: updateDefinitionNameMutateAsync } = trpc.formDesigner.updateDetails.useMutation({
        onSuccess: (updated, submitted) => {
            synchroniseSavedDefinitionDetails(detailsForm, updated, submitted);
            // The autosave's own history invalidation can run before this
            // deferred rename commits its audit row (flushDefinitionNameRename
            // is fire-and-forget) — invalidate again here so an open Audit
            // trail tab always catches the rename.
            void trpcUtils.history.listHistoryItems.invalidate();
        },
        onError: (error) => errorMessage.show(error),
    });
    const {
        queue: queueDefinitionNameRename,
        flush: flushDefinitionNameRename,
        cancel: cancelDefinitionNameRename,
    } = useDeferredDefinitionNameRename(updateDefinitionNameMutateAsync);

    // Autosave — silent draft persistence. The coordinator serialises requests,
    // retains edits made in flight, and latches failures until the next edit.
    // The saved result reaches the cache before invalidation so a remount cannot
    // re-seed from the pre-save snapshot if the subsequent refetch fails.
    const { isAutosaving } = useSerialAutosave({
        enabled: !isNew && latestVersion !== undefined,
        isDirty,
        editVersion,
        getSnapshot: () => {
            if (!latestVersion) return null;
            const schema = getSchemaForSave();
            if (!schema) return null;
            const expectedSnapshotHash = getExpectedSnapshotHash();
            if (!expectedSnapshotHash) return null;
            return {
                versionId: latestVersion.id,
                expectedSnapshotHash,
                runtimeSchema: schema satisfies DehydratedFormSchema as RuntimeSchemaInput,
            };
        },
        save: (snapshot) =>
            autosaveMutateAsync({
                formDefinitionId: BigInt(formDefinitionId),
                versionId: snapshot.versionId,
                expectedSnapshotHash: snapshot.expectedSnapshotHash,
                runtimeSchema: snapshot.runtimeSchema,
            }),
        onLatestSaveSuccess: () => {
            markSchemaSaved();
            // The saved snapshot is the latest edit version, so it carries the
            // title this rename followed — only now can the internal name follow.
            void flushDefinitionNameRename();
        },
        onSaveSuccess: (updated) => {
            markPersistedSnapshot(updated.snapshotHash, updated.version);
            setSaveState('saved');
            // Editing a published version silently forks a new draft server-side —
            // without this, the only trace is the small version badge changing.
            if (latestVersion && updated.version > latestVersion.version) {
                message.info(
                    `Draft v${updated.version} created — v${latestVersion.version} stays published until you publish again`,
                );
            }
            trpcUtils.formDesigner.getFormDefinition.setData({ formDefinitionId: BigInt(formDefinitionId) }, (prev) => {
                if (!prev) return prev;
                const exists = prev.Versions.some((version) => version.id === updated.id);
                return {
                    ...prev,
                    // saveDraft clones to a new version when the draft was
                    // published mid-edit — prepend it so it becomes the latest.
                    Versions: exists
                        ? prev.Versions.map((version) => (version.id === updated.id ? updated : version))
                        : [updated, ...prev.Versions],
                };
            });
            void trpcUtils.formDesigner.getFormDefinition.invalidate();
            void trpcUtils.formDesigner.listVersionHistory.invalidate();
            void trpcUtils.history.listHistoryItems.invalidate();
        },
        onSaveError: (error) => {
            setSaveState('error');
            void refreshFormDefinitionOnAutosaveConflict(error, () =>
                trpcUtils.formDesigner.getFormDefinition.invalidate({
                    formDefinitionId: BigInt(formDefinitionId),
                }),
            );
            errorMessage.show(error);
        },
    });

    const { mutate: publishMutate, isPending: isPublishing } = trpc.formDesigner.publishVersion.useMutation({
        onSuccess: () => {
            message.success('Version published');
            void trpcUtils.formDesigner.getFormDefinition.invalidate();
            void trpcUtils.formDesigner.listVersionHistory.invalidate();
            void trpcUtils.history.listHistoryItems.invalidate();
        },
        onError: (error) => errorMessage.show(error),
    });

    const { mutate: archiveMutate } = trpc.formDesigner.archiveVersion.useMutation({
        onSuccess: () => {
            message.success('Version archived');
            void trpcUtils.formDesigner.getFormDefinition.invalidate();
            void trpcUtils.formDesigner.listVersionHistory.invalidate();
            void trpcUtils.history.listHistoryItems.invalidate();
        },
        onError: (error) => errorMessage.show(error),
    });

    // Create (new forms only — existing drafts autosave).
    const handleCreate = useCallback(() => {
        const schema = getSchemaForSave();
        if (!schema) {
            message.warning('Fix invalid JSON before saving');
            return;
        }
        const values = detailsForm.getFieldsValue();
        const identity = resolveNewDefinitionIdentity({
            detailsName: values.name,
            schemaKey: values.schemaKey,
            schemaTitle,
            defaultSchemaTitle: DEFAULT_SCHEMA.title,
            timestamp: Date.now(),
        });
        createMutate({
            ...identity,
            description: values.description || undefined,
            runtimeSchema: schema satisfies DehydratedFormSchema as RuntimeSchemaInput,
        });
    }, [getSchemaForSave, detailsForm, createMutate, schemaTitle]);

    const handleDetailsSubmit = useCallback(async () => {
        if (isNew) return;
        // validateFields, not getFieldsValue — otherwise the field-level rules
        // (required name, tag length) are decoration and the server does the
        // rejecting. A failed validation shows inline errors and stops here.
        let values: DefinitionDetailsFormValues;
        try {
            values = await detailsForm.validateFields();
        } catch {
            return;
        }
        const input = {
            formDefinitionId: BigInt(formDefinitionId),
            name: values.name,
            description: values.description || null,
            tags: normaliseTags(values.tags),
        };
        // An explicit Settings name owns the final value. Cancel an automatic
        // header follow-up before it can flush; if it is already in flight, wait
        // for it so this explicit write is guaranteed to land last.
        void cancelDefinitionNameRename().then(() => updateDetailsMutate(input));
    }, [isNew, detailsForm, cancelDefinitionNameRename, updateDetailsMutate, formDefinitionId]);

    /**
     * The header renames the form the respondent sees — the schema title.
     *
     * The internal definition name follows along only while the two agree (or
     * while it is still a placeholder), so a form named once in the header does
     * not sit in the definitions list as "Untitled form". Once someone gives the
     * definition its own name in Settings, the header stops touching it.
     */
    const handleInlineRename = useCallback(
        (title: string): boolean => {
            const currentName = String(detailsForm.getFieldValue('name') ?? '').trim();
            return applyInlineFormRename({
                title,
                previousTitle: schemaTitle,
                currentDefinitionName: currentName,
                formDefinitionId,
                isNew,
                /**
                 * One operation that keeps both in-memory representations in step
                 * without changing which is authoritative — either half on its own
                 * loses the rename or the author's unsaved work.
                 */
                updateSchemaTitle: (nextTitle) => {
                    if (renameFormTitle(nextTitle)) return true;
                    // Nothing can carry the rename: writing it to the builder would
                    // discard the in-progress JSON the author is fixing.
                    message.warning('Fix the JSON before renaming the form');
                    return false;
                },
                updateDefinitionName: (nextTitle) => detailsForm.setFieldValue('name', toDefinitionName(nextTitle)),
                /**
                 * Held until the schema save that carries the matching title lands.
                 * Persisting the definition name straight away would leave the two
                 * permanently out of step if that save is then rejected — the list
                 * would show the new name while respondents still see the old title.
                 */
                persistDefinitionName: queueDefinitionNameRename,
            });
        },
        [detailsForm, isNew, formDefinitionId, renameFormTitle, schemaTitle, queueDefinitionNameRename],
    );

    /** Send the user to whatever a health issue actually refers to. */
    const handleOpenHealthIssue = useCallback(
        (target: FormHealthTarget) => {
            // View first: leaving the JSON view syncs the editor text into the
            // builder store, which would otherwise discard a selection set before it.
            switch (target.kind) {
                case 'element':
                    // The aspect the issue is about decides the inspector tab —
                    // a validator problem opening on Field shows no control that
                    // could fix it.
                    setBuildInspectorTarget({
                        panel: 'element-properties',
                        elementName: target.elementName,
                        tab: target.aspect ?? 'field',
                    });
                    handleViewChange('build');
                    builderStore.getState().setSelection({
                        type: 'element',
                        nodeName: target.nodeName,
                        elementName: target.elementName,
                    });
                    break;
                case 'page':
                    // Page properties, not the default Elements toolbox.
                    setBuildInspectorTarget({
                        panel: 'page-properties',
                        tab: target.aspect === 'logic' ? 'logic' : 'page',
                    });
                    handleViewChange('build');
                    builderStore.getState().setSelection({ type: 'page', nodeName: target.nodeName });
                    break;
                case 'form-title':
                    // The header owns the respondent-facing title — open its editor.
                    setRenameRequestId((id) => id + 1);
                    break;
                case 'json':
                    handleViewChange('json');
                    break;
                case 'navigation':
                    handleViewChange('flow');
                    if (target.nodeName !== undefined) {
                        builderStore.getState().setSelection({ type: 'page', nodeName: target.nodeName });
                    }
                    break;
                case 'automation':
                    handleViewChange('logic');
                    builderStore.getState().setAutomationPanelOpen(true);
                    break;
                case 'settings':
                    handleViewChange('settings');
                    break;
            }
        },
        [handleViewChange, builderStore],
    );

    // Reset preview values by cycling the preview surface — the form store is
    // recreated when the preview becomes active again, without touching the
    // dirty state or the authoritative source.
    const handlePreviewReset = useCallback(() => {
        setPreviewOpen(false);
        setTimeout(() => setPreviewOpen(true), 0);
    }, []);

    const applyLoadSample = useCallback(() => {
        builderStore.getState().setSchema(SAMPLE_SCHEMA);
        // Convert builder (nodes) → runtime (pages) for the JSON editor
        const runtimeFull = builderSchemaToRuntime({ ...SAMPLE_SCHEMA, id: 'sample', version: 1 });
        const runtimeDehydrated = dehydrateRuntimeSchema(runtimeFull);
        setEditorJson(JSON.stringify(runtimeDehydrated, null, 2));
        detailsForm.setFieldsValue({
            name: 'Insurance claim intake',
            schemaKey: 'insurance-claim-intake-sample',
            description: 'Sample form demonstrating all element types, branching, computed fields, and automations.',
        });
    }, [builderStore, setEditorJson, detailsForm]);

    const handleLoadSample = useCallback(() => {
        if (isDirty) {
            modal.confirm({
                title: 'Replace current form?',
                content: 'Loading the sample will replace all current content. This cannot be undone.',
                okText: 'Replace',
                okType: 'danger',
                onOk: applyLoadSample,
            });
        } else {
            applyLoadSample();
        }
    }, [isDirty, applyLoadSample]);

    /**
     * Publishing freezes the version and swaps which one is live — enough of a
     * state change to deserve one explicit confirmation, especially with open
     * suggestions that will ship as-is.
     */
    const confirmPublish = useCallback(
        (versionId: bigint, versionNumber: number) => {
            const suggestionCount = healthIssues.filter((issue) => issue.severity !== 'error').length;
            modal.confirm({
                title: `Publish version ${versionNumber}?`,
                content:
                    suggestionCount > 0
                        ? `This freezes v${versionNumber} and makes it the active version. ${suggestionCount} open ${suggestionCount === 1 ? 'suggestion ships' : 'suggestions ship'} with it.`
                        : `This freezes v${versionNumber} and makes it the active version.`,
                okText: 'Publish',
                onOk: () => publishMutate({ versionId }),
            });
        },
        [healthIssues, publishMutate],
    );

    const canPublish = !isNew && latestVersion?.status === 'DRAFT';
    const publishBlockedReason = useMemo(() => {
        if (schemaHasErrors) return 'Resolve the validation issues before publishing';
        if (isDirty || isAutosaving) return 'Waiting for autosave to finish';
        return undefined;
    }, [schemaHasErrors, isDirty, isAutosaving]);
    const publishDisabledReason = useMemo(() => {
        if (!latestVersion) return 'No version is available to publish';
        if (latestVersion.status === 'PUBLISHED') return 'The latest version is already published';
        if (latestVersion.status === 'ARCHIVED') return 'Archived versions cannot be published';
        return publishBlockedReason;
    }, [latestVersion, publishBlockedReason]);

    /**
     * Live shape of the JSON document, for the editor's status line.
     *
     * Derived from `dehydrated` — the parse of the editor text on every keystroke —
     * not from the builder store, which only re-syncs when the view changes and
     * would leave the status line reporting the counts from before this edit.
     */
    const jsonStatus = useMemo(() => {
        if (!dehydrated) return undefined;
        let fields = 0;
        let rules = 0;
        for (const page of dehydrated.pages) {
            fields += page.elements?.length ?? 0;
            rules += page.navigateIf?.length ?? 0;
            rules += Object.keys(page.navigationMap?.valueToPage ?? {}).length;
        }
        return { pages: dehydrated.pages.length, fields, rules };
    }, [dehydrated]);

    const jumpToBuild = useCallback(
        (target?: BuildInspectorTarget) => {
            setBuildInspectorTarget(target);
            handleViewChange('build');
        },
        [handleViewChange],
    );
    const jumpToFlow = useCallback(() => handleViewChange('flow'), [handleViewChange]);
    if (isLoading && !isNew) {
        return (
            <PageController route={Route}>
                <Loading size="large" className="py-16" />
            </PageController>
        );
    }

    // Surface a failed load instead of silently rendering the blank "new form"
    // defaults over an existing definition.
    if (definitionError && !isNew) {
        return (
            <PageController route={Route}>
                <Alert
                    type="error"
                    title="Failed to load form definition"
                    description={definitionError.message}
                    showIcon
                    className="m-4"
                />
            </PageController>
        );
    }

    return (
        <PageController route={Route}>
            <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
                {/* One editor bar: identity left, view switcher centred, actions right.
                    The centre is the only slot nothing else competes for, so the switcher
                    reads as navigation rather than as a property of the form. */}
                <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-separator border-b px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-2 justify-self-start">
                        <Button.Link
                            aria-label="Back to form designer"
                            icon={<ArrowLeft size={16} />}
                            to="/console/$orgId/configuration/api-integrations/form-designer"
                            params={{ orgId }}
                        />
                        <InlineFormNameEditor
                            name={schemaTitle}
                            onRename={handleInlineRename}
                            editRequestId={renameRequestId}
                        />
                        {latestVersion && (
                            <StatusBadge
                                status={latestVersion.status}
                                module="formSchema"
                                customLabel={`v${latestVersion.version} · ${getStatusLabel('formSchema', latestVersion.status)}`}
                            />
                        )}
                        {/* Autosave status — drafts persist on their own */}
                        {!isNew &&
                            (isAutosaving ? (
                                <span className="shrink-0 text-muted-foreground text-xs">Saving…</span>
                            ) : hasRemoteUpdate ? (
                                <span className="shrink-0 text-warning-foreground text-xs">
                                    A newer draft is available — choose which version to keep
                                </span>
                            ) : isDirty && isStaleSnapshot ? (
                                <span className="shrink-0 text-warning-foreground text-xs">
                                    Invalid JSON — fix it in the JSON view to save
                                </span>
                            ) : saveState === 'error' ? (
                                <span className="shrink-0 text-danger text-xs">
                                    Autosave failed — retrying on next edit
                                </span>
                            ) : saveState === 'saved' && !isDirty ? (
                                <span className="shrink-0 text-muted-foreground text-xs">Saved just now</span>
                            ) : null)}
                    </div>
                    <SegmentedControl
                        value={view}
                        onChange={handleViewChange}
                        size="middle"
                        variant="default"
                        aria-label="Editor view"
                        className="w-max justify-self-center"
                        options={[
                            { value: 'build', label: 'Build', icon: <Blocks size={16} /> },
                            { value: 'flow', label: 'Flow', icon: <Workflow size={16} /> },
                            { value: 'logic', label: 'Logic', icon: <ListChecks size={16} /> },
                            { value: 'json', label: 'JSON', icon: <Braces size={16} /> },
                            { value: 'settings', label: 'Settings', icon: <Settings size={16} /> },
                        ]}
                    />

                    <div className="flex shrink-0 items-center gap-2 justify-self-end">
                        {/* Undo / redo — builder schema history; hidden in the JSON view
                            (the code editor has its own undo) and in Settings. Real
                            32px icon buttons with design-system tooltips, so the header
                            speaks one control idiom rather than three. */}
                        {view !== 'settings' && view !== 'json' && (
                            <div className="flex items-center gap-0.5">
                                <Button.Icon
                                    color="gray"
                                    tooltip="Undo (Cmd+Z)"
                                    aria-label="Undo"
                                    icon={<Undo2 />}
                                    onClick={() => builderStore.getState().undo()}
                                    disabled={!canUndo}
                                />
                                <Button.Icon
                                    color="gray"
                                    tooltip="Redo (Cmd+Shift+Z)"
                                    aria-label="Redo"
                                    icon={<Redo2 />}
                                    onClick={() => builderStore.getState().redo()}
                                    disabled={!canRedo}
                                />
                            </div>
                        )}

                        {/* Whole-form health — it gates Publish, so it sits beside it,
                            and it opens the list of what is actually wrong. */}
                        <FormHealthPopover
                            issues={healthIssues}
                            onOpenIssue={handleOpenHealthIssue}
                            onViewAll={() => handleViewChange('logic')}
                        />

                        {isNew && (
                            <Button icon={<FlaskConical size={16} />} onClick={handleLoadSample}>
                                Load sample
                            </Button>
                        )}
                        {!isNew && (
                            <Button
                                type="tertiary"
                                icon={<HistoryIcon size={16} />}
                                onClick={() => setHistoryOpen(true)}
                            >
                                History
                            </Button>
                        )}
                        <Button icon={<Eye size={16} />} onClick={() => setPreviewOpen(true)}>
                            Preview
                        </Button>
                        {/* A disabled antd Button swallows pointer events, so the reason
                            it is disabled hangs off a wrapper rather than the button —
                            otherwise the explanation is unreachable exactly when it matters. */}
                        {isNew ? (
                            <Tooltip title={isStaleSnapshot ? 'Fix invalid JSON before saving' : undefined}>
                                <span className="inline-flex">
                                    <Button
                                        type="primary"
                                        icon={<Save size={16} />}
                                        loading={isCreating}
                                        onClick={handleCreate}
                                        disabled={isStaleSnapshot}
                                    >
                                        Create
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title={publishDisabledReason}>
                                <span className="inline-flex">
                                    <Button
                                        type="primary"
                                        icon={<Rocket size={16} />}
                                        loading={isPublishing}
                                        onClick={() =>
                                            latestVersion && confirmPublish(latestVersion.id, latestVersion.version)
                                        }
                                        disabled={!canPublish || publishBlockedReason !== undefined}
                                    >
                                        Publish
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {hasRemoteUpdate && (
                    <Alert
                        type="warning"
                        title="Another author saved a newer draft while you had unsaved changes."
                        description="Choose whether to keep your local edits or reload the latest saved draft. Autosave is paused until you decide."
                        action={
                            <Space size="small">
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                        keepLocalChanges();
                                        setSaveState('idle');
                                    }}
                                >
                                    Keep my edits
                                </Button>
                                <Button
                                    size="small"
                                    type="secondary"
                                    danger
                                    onClick={() => {
                                        reloadRemoteSnapshot();
                                        setSaveState('idle');
                                    }}
                                >
                                    Reload latest
                                </Button>
                            </Space>
                        }
                        className="shrink-0 rounded-none"
                        showIcon
                    />
                )}

                {isStaleSnapshot && view !== 'json' && (
                    <Alert
                        type="warning"
                        title="The JSON source contains invalid JSON. Showing last valid schema snapshot."
                        className="shrink-0 rounded-none"
                        showIcon
                    />
                )}

                {/* Active view — fills the shell edge to edge */}
                <div className="min-h-0 flex-1">
                    {view === 'build' && (
                        <VisualBuilder
                            store={builderStore}
                            formDefinitionId={isNew ? 'new' : formDefinitionId}
                            version={latestVersion?.version ?? 1}
                            validation={builderValidation}
                            onOpenFlow={jumpToFlow}
                            inspectorTarget={buildInspectorTarget}
                            onInspectorTargetHandled={() => setBuildInspectorTarget(undefined)}
                        />
                    )}
                    {view === 'flow' && <FlowView store={builderStore} validation={builderValidation} />}
                    {view === 'logic' && (
                        <LogicOverview store={builderStore} onJumpToBuild={jumpToBuild} onJumpToFlow={jumpToFlow} />
                    )}
                    {view === 'json' && (
                        <div className="h-full min-h-0 p-4">
                            <BaseSchemaEditor
                                height="100%"
                                schemaJson={editorJson}
                                parseError={parseError}
                                parseErrorKind={parseErrorKind ?? undefined}
                                copied={copied}
                                status={jsonStatus}
                                onSchemaChange={setEditorJson}
                                onFormatJson={formatJson}
                                onCopySchema={copySchema}
                            />
                        </div>
                    )}
                    {/* Settings stays mounted (hidden) so the details Form instance
                        remains connected — its values feed the Create action. */}
                    <div
                        className={view === 'settings' ? 'h-full min-h-0 overflow-y-auto bg-background p-4' : 'hidden'}
                    >
                        <Card className="mx-auto max-w-2xl">
                            <Form
                                form={detailsForm}
                                id={formId}
                                layout="vertical"
                                initialValues={{
                                    name: isNew ? '' : definition?.name,
                                    description: isNew ? '' : definition?.description,
                                    schemaKey: isNew ? '' : definition?.schemaKey,
                                    tags: isNew ? [] : (definition?.tags ?? []),
                                }}
                                onValuesChange={(changed) => {
                                    if (isNew && 'name' in changed) {
                                        detailsForm.setFieldValue('schemaKey', toSchemaKey(String(changed.name ?? '')));
                                    }
                                }}
                            >
                                <Form.Item
                                    label="Form name"
                                    name="name"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                    extra="Internal name, used in the form designer list. The title respondents see is the one in the editor header."
                                >
                                    <Input placeholder="e.g. Annual compliance declaration" />
                                </Form.Item>
                                <Form.Item label="Description" name="description">
                                    <Input.TextArea rows={3} placeholder="Brief description of this form" />
                                </Form.Item>
                                <Form.Item
                                    label="Schema key"
                                    name="schemaKey"
                                    rules={[
                                        { required: true, message: 'Schema key is required' },
                                        {
                                            pattern: /^[a-z0-9-]+$/,
                                            message: 'Must be lowercase alphanumeric with hyphens',
                                        },
                                    ]}
                                    extra={
                                        isNew
                                            ? 'Auto-generated from form name'
                                            : 'Schema key cannot be changed after creation'
                                    }
                                >
                                    <Input placeholder="e.g. annual-compliance" disabled={!isNew} />
                                </Form.Item>
                                {!isNew && (
                                    <Form.Item
                                        label="Tags"
                                        name="tags"
                                        extra="Shown in the forms list — up to 10 tags"
                                        rules={[
                                            {
                                                validator: (_rule, value: string[] | undefined) => {
                                                    if (value?.some((tag) => tag.trim().length > 30)) {
                                                        return Promise.reject(
                                                            new Error('Tags must be 30 characters or fewer'),
                                                        );
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Select
                                            mode="tags"
                                            maxCount={10}
                                            placeholder="Type a tag and press Enter"
                                            suffixIcon={null}
                                            popupMatchSelectWidth={false}
                                            notFoundContent={null}
                                        />
                                    </Form.Item>
                                )}
                                {!isNew && (
                                    <Button type="primary" onClick={handleDetailsSubmit}>
                                        Save details
                                    </Button>
                                )}
                            </Form>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Preview — a drawer for quick checks, expandable to the full viewport
                for judging real layout and line lengths. */}
            <Drawer
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                title="Preview"
                size={previewFullScreen ? '100%' : 760}
                extra={
                    <Button
                        size="small"
                        type="tertiary"
                        icon={previewFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        onClick={() => setPreviewFullScreen((full) => !full)}
                    >
                        {previewFullScreen ? 'Exit full screen' : 'Full screen'}
                    </Button>
                }
            >
                <BaseSchemaPreview
                    className="h-full"
                    parsedSchema={hydrated}
                    formStore={formStore}
                    onReset={handlePreviewReset}
                />
            </Drawer>

            <HistoryDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                formDefinitionId={formDefinitionId}
                isNew={isNew}
                hasDefinition={definition !== undefined}
                versions={versionHistoryRows}
                isLoadingVersions={isLoadingVersionHistory}
                versionsError={versionHistoryError}
                isFetchNextPageError={isFetchNextPageError}
                hasMoreVersions={hasMoreVersions}
                isFetchingMoreVersions={isFetchingMoreVersions}
                onRetryVersions={() => void refetchVersionHistory()}
                onLoadMoreVersions={() => void fetchMoreVersions()}
                formatDate={formatDate}
                isPublishing={isPublishing}
                publishBlockedReason={publishBlockedReason}
                onPublish={confirmPublish}
                onArchive={(versionId) => archiveMutate({ versionId, reason: 'Manually archived' })}
            />
        </PageController>
    );
}
